// CE-04g-UI-pivots-popover — pure helper. Returns a per-level boolean map
// indicating whether each pivot axis label should be VISIBLE on the LWC
// price axis. Higher-priority levels suppress lower-priority ones when
// their display prices collide (within minMove/2). The chart's built-in
// current-price label is treated as a peer with priority ABOVE all pivots:
// when a pivot collides with `currentClose`, the pivot's axis label is
// suppressed (we cannot toggle the built-in current-price label).
//
// Priority chain: pivot > r1 > s1 > r2 > s2 > r3 > s3.

const LEVEL_KEYS = ['pivot', 'r1', 'r2', 'r3', 's1', 's2', 's3']
const PRIORITY_ORDER = ['pivot', 'r1', 's1', 'r2', 's2', 'r3', 's3']

/**
 * @param {object} levels  { pivot?, r1?, r2?, r3?, s1?, s2?, s3? } (numbers)
 * @param {number|null} currentClose  the chart's current price (last close)
 * @param {number} priceScaleMinMove  e.g. 0.01
 * @returns {{ pivot:boolean, r1:boolean, r2:boolean, r3:boolean, s1:boolean, s2:boolean, s3:boolean }}
 */
export function pickVisibleAxisLabels(levels, currentClose, priceScaleMinMove) {
  const result = {}
  for (const k of LEVEL_KEYS) result[k] = true

  const finite = (v) => typeof v === 'number' && Number.isFinite(v)
  const safeMin = finite(priceScaleMinMove) && priceScaleMinMove > 0 ? priceScaleMinMove : 0.01
  const tolerance = safeMin * 0.5
  const collides = (a, b) => Math.abs(a - b) < tolerance

  // Walk PRIORITY_ORDER. For each level present + finite, check against
  // (a) every higher-priority level already kept visible, and (b) the
  // current close. If a collision is found, mark THIS level as suppressed.
  const keptVisible = []
  for (const key of PRIORITY_ORDER) {
    const v = levels?.[key]
    if (!finite(v)) {
      result[key] = false
      continue
    }
    let suppressed = false
    if (finite(currentClose) && collides(v, currentClose)) suppressed = true
    if (!suppressed) {
      for (const higher of keptVisible) {
        if (collides(v, higher.value)) { suppressed = true; break }
      }
    }
    if (suppressed) {
      result[key] = false
    } else {
      keptVisible.push({ key, value: v })
    }
  }

  return result
}
